import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../../ThemeContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { formatDate } from '../../helpers/UtilsFunctions';

const TodaysTasksTable = ({ todaysTasks }) => {
  const { mode } = useThemeMode();
  const { hasReadPermission } = usePermissions();
  const navigate = useNavigate();

  // Process today's tasks data from API
  const tasks = todaysTasks || [];

  // Pagination: show 5 rows per page
  const pageSize = 5;
  const [page, setPage] = useState(0);
  const totalPages = useMemo(() => Math.ceil((tasks?.length || 0) / pageSize), [tasks?.length]);
  const pagedTasks = useMemo(() => {
    const start = page * pageSize;
    return tasks.slice(start, start + pageSize);
  }, [tasks, page]);

  // Handle task click to navigate to task details
  const handleTaskClick = (task) => {
    // Only navigate if user has read permission for tasks
    if (!hasReadPermission('task')) {
      return;
    }
    
    navigate(`/project/${task.project_id}/task/${task.id}`, {
      state: {
        fromDashboard: true,
        projectName: task.project_name
      }
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High':
        return '#F44336';
      case 'Medium':
        return '#2196F3';
      case 'Low':
        return '#FF9800';
      default:
        return '#666666';
    }
  };

  return (
    <div style={{
      backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
      borderRadius: '12px',
      padding: '24px',
      border: mode === 'dark' ? '1px solid #333' : '1px solid #e0e0e0',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '20px',
        flexShrink: 0
      }}>
        <h3 style={{
          fontSize: '20px',
          fontWeight: '600',
          margin: 0,
          color: mode === 'dark' ? '#ffffff' : '#333333',
          fontFamily: 'Urbanist, Arial, sans-serif'
        }}>
          Todays Task List
        </h3>
      </div>

      {/* Table Container with Scroll */}
      <div style={{
        flex: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Table Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '60px 1fr 120px 80px 120px',
          gap: '12px',
          padding: '12px 0',
          borderBottom: mode === 'dark' ? '1px solid #333' : '1px solid #e0e0e0',
          marginBottom: '8px',
          flexShrink: 0
        }}>
          <div style={{
            fontSize: '12px',
            fontWeight: '600',
            color: mode === 'dark' ? '#888888' : '#666666',
            fontFamily: 'Urbanist, Arial, sans-serif',
            textTransform: 'uppercase'
          }}>
            #
          </div>
          <div style={{
            fontSize: '12px',
            fontWeight: '600',
            color: mode === 'dark' ? '#888888' : '#666666',
            fontFamily: 'Urbanist, Arial, sans-serif',
            textTransform: 'uppercase'
          }}>
            Task Title
          </div>
          <div style={{
            fontSize: '12px',
            fontWeight: '600',
            color: mode === 'dark' ? '#888888' : '#666666',
            fontFamily: 'Urbanist, Arial, sans-serif',
            textTransform: 'uppercase'
          }}>
            Assigned To
          </div>
          <div style={{
            fontSize: '12px',
            fontWeight: '600',
            color: mode === 'dark' ? '#888888' : '#666666',
            fontFamily: 'Urbanist, Arial, sans-serif',
            textTransform: 'uppercase'
          }}>
            Priority
          </div>
          <div style={{
            fontSize: '12px',
            fontWeight: '600',
            color: mode === 'dark' ? '#888888' : '#666666',
            fontFamily: 'Urbanist, Arial, sans-serif',
            textTransform: 'uppercase'
          }}>
            Due Date
          </div>
        </div>

        {/* Task Rows */}
        <div>
          {pagedTasks.length > 0 ? pagedTasks.map((task, idx) => {
            const index = page * pageSize + idx;
            const dueDate = formatDate(task.due_date);
            // Generate avatar initials from assigned user name
            const initials = task.assigned_user?.name
              ? task.assigned_user.name.split(' ').map(word => word[0]).join('').toUpperCase()
              : 'NA';
            
            return (
              <div
                key={`${task.id}-${index}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '60px 1fr 120px 80px 120px',
                  gap: '10px',
                  padding: '8px 0',
                  borderBottom: index < tasks.length - 1 ? (mode === 'dark' ? '1px solid #333' : '1px solid #e0e0e0') : 'none',
                  alignItems: 'center',
                  cursor: hasReadPermission('task') ? 'pointer' : 'default'
                }}
                onClick={() => handleTaskClick(task)}
                title="Click to view task details"
              >
                {/* Serial Number */}
                <div style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: mode === 'dark' ? '#ffffff' : '#333333',
                  fontFamily: 'Urbanist, Arial, sans-serif'
                }}>
                  {index + 1}
                </div>

                {/* Task Title */}
                <div style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: mode === 'dark' ? '#ffffff' : '#333333',
                  fontFamily: 'Urbanist, Arial, sans-serif'
                }}>
                  {task.task_title}
                </div>

                {/* Assigned To */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: '#26c3c0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    fontWeight: '600',
                    color: '#ffffff',
                    fontFamily: 'Urbanist, Arial, sans-serif'
                  }}>
                    {initials}
                  </div>
                  <span style={{
                    fontSize: '13px',
                    color: mode === 'dark' ? '#cccccc' : '#666666',
                    fontFamily: 'Urbanist, Arial, sans-serif'
                  }}>
                    {task.assigned_user?.name || 'Unassigned'}
                  </span>
                </div>

                {/* Priority */}
                <div>
                  <span style={{
                    backgroundColor: getPriorityColor(task.priority),
                    color: '#ffffff',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500',
                    fontFamily: 'Urbanist, Arial, sans-serif'
                  }}>
                    {task.priority}
                  </span>
                </div>

                {/* Due Date */}
                <div style={{
                  fontSize: '14px',
                  color: mode === 'dark' ? '#cccccc' : '#666666',
                  fontFamily: 'Urbanist, Arial, sans-serif'
                }}>
                  {dueDate}
                </div>
              </div>
            );
          }) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100px',
              color: mode === 'dark' ? '#888888' : '#666666',
              fontSize: '14px',
              fontFamily: 'Urbanist, Arial, sans-serif'
            }}>
              No tasks found for today
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: '8px',
            marginTop: '12px'
          }}>
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              style={{
                padding: '6px 10px',
                borderRadius: '6px',
                border: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`,
                background: 'transparent',
                color: mode === 'dark' ? '#fff' : '#333',
                cursor: page === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              Prev
            </button>
            <span style={{ fontSize: '12px', color: mode === 'dark' ? '#aaa' : '#666' }}>
              Page {page + 1} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              style={{
                padding: '6px 10px',
                borderRadius: '6px',
                border: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`,
                background: 'transparent',
                color: mode === 'dark' ? '#fff' : '#333',
                cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer'
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TodaysTasksTable;
